import superjson from "superjson"
import * as Network from "expo-network"
import { fetch } from "react-native-nitro-fetch"
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server"
import { httpBatchLink, httpLink, isNonJsonSerializable, splitLink } from "@trpc/client"
import { QueryClient, QueryClientProvider, onlineManager } from "@tanstack/react-query"
import { createTRPCReact, type inferReactQueryProcedureOptions, TRPCClientError } from "@trpc/react-query"

import { getToken } from "./storage"
import { ENV } from "./env"
import type { AppRouter } from "../../../api/m5azen/router"

onlineManager.setEventListener((setOnline) => {
  const eventSubscription = Network.addNetworkStateListener((state) => {
    setOnline(!!state.isConnected)
  })
  return eventSubscription.remove
})

export type ReactQueryOptions = inferReactQueryProcedureOptions<AppRouter>
export type RouterInputs = inferRouterInputs<AppRouter>
export type RouterOutputs = inferRouterOutputs<AppRouter>

export const trpc = createTRPCReact<AppRouter>()

export const trpcClient = trpc.createClient({
  links: [
    splitLink({
      condition: (op) => isNonJsonSerializable(op.input),
      true: httpLink({
        url: ENV.BASE_URL,
        headers() {
          const token = getToken()
          return { ...(token ? { authorization: token } : {}) }
        },
        fetch: fetch,
        transformer: {
          // request - convert data before sending to the tRPC server
          serialize: (data) => data,
          // response - convert the tRPC response before using it in client
          deserialize: superjson.deserialize,
        },
      }),
      false: httpBatchLink({
        headers() {
          const token = getToken()
          return { ...(token ? { authorization: token } : {}) }
        },
        transformer: superjson,
        fetch: fetch,
        url: ENV.BASE_URL,
      }),
    }),
  ],
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: Infinity,
      staleTime: 1000 * 60 * 5,
      retry(failureCount, error) {
        if (error instanceof TRPCClientError && error.data?.httpStatus === 401) {
          return false
        }
        return failureCount < 3
      },
    },
  },
})

export function TRPCProvider({ children }: { children: React.ReactNode }) {
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  )
}
